export type PerformanceDeletion = {
  /** Whether the deletion can go ahead; otherwise the popup only explains why. */
  canDelete: boolean;
  title: string;
  message: string;
};

/**
 * Describes the outcome of deleting a performance, so the popup
 * states it up front instead of offering a confirmation the API would refuse:
 * - in preparation, the attached interventions are deleted along with it;
 * - outside preparation, it is only possible while no intervention is attached.
 */
export function describePerformanceDeletion(
  name: string,
  interventionCount: number,
  inPreparation: boolean,
): PerformanceDeletion {
  if (!inPreparation && interventionCount > 0) {
    return {
      canDelete: false,
      title: "Suppression impossible",
      message:
        interventionCount === 1
          ? `La prestation « ${name} » ne peut pas être supprimée : le chantier n'est plus en période de préparation et une intervention y est rattachée.`
          : `La prestation « ${name} » ne peut pas être supprimée : le chantier n'est plus en période de préparation et ${interventionCount} interventions y sont rattachées.`,
    };
  }

  if (interventionCount === 0) {
    return {
      canDelete: true,
      title: "Confirmer la suppression",
      message: `Vous êtes sur le point de supprimer la prestation « ${name} ». Aucune intervention n'y est rattachée.`,
    };
  }

  return {
    canDelete: true,
    title: "Confirmer la suppression",
    message:
      interventionCount === 1
        ? `Vous êtes sur le point de supprimer la prestation « ${name} » ainsi que l'intervention et les affectations qui y sont rattachées. Cette action est irréversible.`
        : `Vous êtes sur le point de supprimer la prestation « ${name} » ainsi que les ${interventionCount} interventions et les affectations qui y sont rattachées. Cette action est irréversible.`,
  };
}

/** Why a performance can't change lot: a move needs nothing attached to it (N76). */
export const performanceMoveRefusal = (
  name: string,
  interventionCount: number,
): string =>
  interventionCount === 1
    ? `« ${name} » ne peut pas changer de lot : une intervention y est rattachée.`
    : `« ${name} » ne peut pas changer de lot : ${interventionCount} interventions y sont rattachées.`;
